import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Group,
    Modal,
    MultiSelect,
    NumberInput,
    Select,
    Stack,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { Info } from '@phosphor-icons/react';

import { getEntityMasterFilterOptions } from '@api/entity-attributes';
import { createVendor, VENDOR_CAPABILITY_LABEL, type Vendor, type VendorCapability } from '@api/vendors';
import { usePoPrefixOptions } from '@components/containers/suppliers/usePoPrefixOptions';
import { readApiError } from '@utils/apiError';
import { toOptionalNumber } from '@utils/number';

interface AddVendorModalProps {
    opened: boolean;
    onClose: () => void;
    /**
     * Fired after a successful create, carrying the row the server actually wrote.
     *
     * The grid ignores it and just reloads. A production order uses it to fill its manufacturer
     * block on the spot — `POST /vendors` returns the whole vendor, so there is nothing to
     * fetch afterwards.
     */
    onCreated: (vendor: Vendor) => void;
    /** The name and capabilities to start on, when the caller already knows who is being added. */
    initialName?: string;
    initialCapabilities?: VendorCapability[];
}

/**
 * The capabilities a vendor can be filed under. This IS the type picker: `vendors.type` used to
 * be a free-text Select seeded with made-up labels ("Distributor"), and nothing downstream read
 * any of them except SALES_VENDOR. Sourcing, make-vs-buy and forwarder booking all read the
 * `capabilities` array, so that is what the form asks for — multi-select, because a vendor can
 * both supply RM and manufacture.
 *
 * `type` is still written, derived from the picked capabilities, so the Suppliers grid column
 * and entity search keep showing something and no placeholder "Default" is minted here.
 * Folding `type` away entirely is an open point (SALES_VENDOR still lives there).
 */
const CAPABILITY_OPTIONS: VendorCapability[] = ['MANUFACTURER', 'RM_SUPPLIER', 'FG_SUPPLIER', 'FREIGHT_FORWARDER'];

const TYPE_LABEL: Record<VendorCapability, string> = {
    MANUFACTURER: 'Manufacturer',
    RM_SUPPLIER: 'RM / PM Supplier',
    FG_SUPPLIER: 'FG Supplier',
    FREIGHT_FORWARDER: 'Freight Forwarder',
};

const typeFromCapabilities = (caps: VendorCapability[]): string =>
    CAPABILITY_OPTIONS.filter((c) => caps.includes(c)).map((c) => TYPE_LABEL[c]).join(' / ');

/** Incoterms in use across the live catalogue, as the fallback list. */
const SUGGESTED_INCOTERMS = ['EXW', 'FOB', 'CIF', 'DAP', 'DDP'];

/**
 * The code a vendor is filed under, worked out from the name it is called by.
 *
 * THIS USED TO BE A FIELD, and it was the wrong question. "Vendor code" means nothing to
 * somebody adding Apricot in the middle of raising an order - they either typed the name twice
 * or invented something, and the invented ones drift. The name is the only thing they actually
 * know, so the code comes off it: Apricot -> APRICOT.
 *
 * Uppercased and stripped to letters, digits and underscores, because this value is the primary
 * key and joins purchase orders, mappings and lanes - punctuation in a join key is a quiet
 * source of near-duplicates.
 */
const codeFromName = (name: string): string =>
    name
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 32);

/**
 * Merges the merchant's own values ahead of the suggested ones, de-duplicated
 * case-insensitively, so a merchant already holding 'FOB' is not offered it twice.
 */
const mergeOptions = (own: string[], suggested: string[]): string[] => {
    const seen = new Set<string>();
    const options: string[] = [];

    for (const value of [...own, ...suggested]) {
        const key = value.trim().toLowerCase();
        if (!key || key === 'default' || seen.has(key)) continue;
        seen.add(key);
        options.push(value);
    }

    return options;
};

const AddVendorModal: React.FC<AddVendorModalProps> = ({
    opened,
    onClose,
    onCreated,
    initialName,
    initialCapabilities,
}) => {
    const [name, setName] = useState('');
    const [legalName, setLegalName] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [zip, setZip] = useState('');
    const [taxCode, setTaxCode] = useState('');
    const [email, setEmail] = useState('');
    const [incoterms, setIncoterms] = useState<string | null>(null);
    const [paymentTerms, setPaymentTerms] = useState('');
    const [moq, setMoq] = useState<string | number>('');
    // No default: 'Default' is exactly the unclassified state this form exists to
    // avoid, so the field starts empty and has to be answered.
    const [capabilities, setCapabilities] = useState<VendorCapability[]>([]);
    const [poNumberPrefix, setPoNumberPrefix] = useState<string | null>(null);
    const poPrefixOptions = usePoPrefixOptions(opened);

    const [incotermOptions, setIncotermOptions] =
        useState<string[]>(SUGGESTED_INCOTERMS);
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [countryOptions, setCountryOptions] = useState<string[]>([]);

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setName('');
        setLegalName('');
        setCapabilities([]);
        setStreet('');
        setCity('');
        setCountry('');
        setZip('');
        setTaxCode('');
        setEmail('');
        setIncoterms(null);
        setPaymentTerms('');
        setMoq('');
        setPoNumberPrefix(null);
        setFieldErrors({});
        setSaveError(null);
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    /*
     * Every picker is filled from the entity-master filter options, which already
     * returns merchant-scoped DISTINCT values for each filterable column. That is
     * what keeps the vocabulary the merchant's own rather than a list we invented —
     * and it needs no bespoke picker endpoint.
     */
    useEffect(() => {
        if (!opened) return;

        if (initialName) setName(initialName);
        if (initialCapabilities?.length) setCapabilities(initialCapabilities);

        void getEntityMasterFilterOptions('VENDOR')
            .then((options) => {
                setIncotermOptions(
                    mergeOptions(options.incoterms ?? [], SUGGESTED_INCOTERMS),
                );
                setCityOptions(options.city ?? []);
                setCountryOptions(options.country ?? []);
            })
            .catch(() => {
                setIncotermOptions(SUGGESTED_INCOTERMS);
                setCityOptions([]);
                setCountryOptions([]);
            });
    }, [opened, initialName, initialCapabilities]);

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!name.trim()) errors.name = 'Name is required';
        // The name still has to survive being turned into a key: "!!!" is something somebody
        // can type and nothing can be filed under.
        else if (!codeFromName(name)) errors.name = 'Use at least one letter or number';
        if (capabilities.length === 0) errors.capabilities = 'Pick at least one thing this vendor does for you';
        if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
            errors.email = 'That does not look like an email address';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setSaving(true);
        setSaveError(null);
        try {
            const created = await createVendor({
                vendorId: codeFromName(name),
                name: name.trim(),
                legalName: legalName.trim() || undefined,
                type: typeFromCapabilities(capabilities),
                street: street.trim() || undefined,
                city: city.trim() || undefined,
                country: country.trim() || undefined,
                zip: zip.trim() || undefined,
                taxCode: taxCode.trim() || undefined,
                email: email.trim() || undefined,
                incoterms: incoterms?.trim() || undefined,
                paymentTerms: paymentTerms.trim() || undefined,
                moq: toOptionalNumber(moq),
                capabilities,
                poNumberPrefix: poNumberPrefix ?? undefined,
            });
            onCreated(created);
            handleClose();
        } catch (err) {
            setSaveError(
                readApiError(err, 'Could not add the vendor. Please try again.'),
            );
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Add vendor"
            size="lg"
            centered
        >
            <Stack gap="md">
                <Alert icon={<Info size={16} />} color="blue" variant="light">
                    <Text size="xs">
                        Only the name and type are needed now. Everything else can be
                        filled in later, in the grid.
                    </Text>
                </Alert>

                {/*
                  * Two fields on a row, and both carry a description or neither does — a
                  * description under one of a pair pushes its input down and the row reads as
                  * a mistake. That is what the old Vendor code / Vendor name pair did.
                  */}
                <Group grow align="flex-start">
                    <TextInput
                        label="Name"
                        description="What you call them day to day"
                        placeholder="Apricot"
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        error={fieldErrors.name}
                        withAsterisk
                    />
                    <MultiSelect
                        label="Type"
                        description="What this vendor does for you. Pick every one that applies."
                        placeholder="Choose one or more"
                        data={CAPABILITY_OPTIONS.map((c) => ({ value: c, label: VENDOR_CAPABILITY_LABEL[c] }))}
                        value={capabilities}
                        onChange={(v) => setCapabilities(v as VendorCapability[])}
                        error={fieldErrors.capabilities}
                        comboboxProps={{ withinPortal: true, zIndex: 10001 }}
                        withAsterisk
                        searchable
                        clearable
                    />
                </Group>

                {/*
                  * The registered company, grouped with the GSTIN and the address because those
                  * three belong to the same legal party — and it is the one a production order
                  * prints. Optional: plenty of suppliers are known only by a working name, and
                  * demanding the registered one would stop somebody adding a vendor at all.
                  */}
                <Group grow align="flex-start">
                    {/* Picking this vendor on a new order fills the number from this stem. */}
                    <Select
                        label="PO number prefix"
                        description="One of your order-numbering prefixes. New orders to this vendor start from it."
                        data={poPrefixOptions}
                        value={poNumberPrefix}
                        onChange={setPoNumberPrefix}
                        placeholder={poPrefixOptions.length ? 'Optional' : 'No prefixes in Settings yet'}
                        disabled={poPrefixOptions.length === 0}
                        clearable
                        searchable
                    />
                </Group>

                <TextInput
                    label="Legal entity name"
                    description="The registered company, if different from the name above"
                    placeholder="Apricot Foods Private Limited"
                    value={legalName}
                    onChange={(e) => setLegalName(e.currentTarget.value)}
                />

                <TextInput
                    label="Street"
                    placeholder="12 Industrial Estate"
                    value={street}
                    onChange={(e) => setStreet(e.currentTarget.value)}
                />

                <Group grow align="flex-start">
                    <Autocomplete
                        label="City"
                        placeholder="Bengaluru"
                        data={cityOptions}
                        value={city}
                        onChange={setCity}
                    />
                    <Autocomplete
                        label="Country"
                        placeholder="India"
                        data={countryOptions}
                        value={country}
                        onChange={setCountry}
                    />
                    <TextInput
                        label="Zip"
                        placeholder="560001"
                        value={zip}
                        onChange={(e) => setZip(e.currentTarget.value)}
                    />
                </Group>

                {/* Beside the address rather than with the commercial terms, because those are
                    the four things a production order prints together as one party. */}
                <TextInput
                    label="GST number"
                    description="Printed on production orders and invoices"
                    placeholder="29AABCT1332L000"
                    value={taxCode}
                    onChange={(e) => setTaxCode(e.currentTarget.value)}
                />

                <Group grow align="flex-start">
                    <TextInput
                        label="Email"
                        placeholder="orders@avon.example"
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        error={fieldErrors.email}
                    />
                    <Select
                        label="Incoterms"
                        placeholder="Choose"
                        data={incotermOptions}
                        value={incoterms}
                        onChange={setIncoterms}
                        clearable
                        searchable
                    />
                    <NumberInput
                        label="MoQ"
                        placeholder="200"
                        value={moq}
                        onChange={(value) =>
                            setMoq(value === '' ? '' : Number(value))
                        }
                        min={0}
                        allowNegative={false}
                    />
                </Group>

                <Textarea
                    label="Payment terms"
                    description="Free text — it is shown as written on orders"
                    placeholder="30% deposit on order, 70% before dispatch"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.currentTarget.value)}
                    minRows={2}
                    autosize
                />

                {saveError && (
                    <Alert color="red" variant="light">
                        <Text size="xs">{saveError}</Text>
                    </Alert>
                )}

                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button loading={saving} onClick={handleSubmit}>
                        Add vendor
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddVendorModal;
